import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { KBFolder } from "@/lib/knowledge-base-service";

const REGULATION_TYPES = [
  "Data Privacy & GDPR",
  "HR & Employment Law",
  "Financial Compliance",
  "Health & Safety",
  "Industry-Specific Regulations",
  "Environmental",
  "Anti-Bribery & Corruption",
  "Information Security",
  "Consumer Protection",
  "Other",
];

const JURISDICTIONS = [
  "United Kingdom",
  "European Union",
  "United States",
  "Canada",
  "Australia",
  "Singapore",
  "South Africa",
  "United Arab Emirates",
  "Global / International",
  "Other",
];

interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: KBFolder[];
  defaultFolderId?: string | null;
  onUpload: (
    file: File,
    meta: { folderId?: string | null; jurisdiction?: string; regulationType?: string }
  ) => Promise<void>;
}

export function UploadDocumentDialog({
  open,
  onOpenChange,
  folders,
  defaultFolderId,
  onUpload,
}: UploadDocumentDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [folderId, setFolderId] = useState<string>(defaultFolderId ?? "none");
  const [jurisdiction, setJurisdiction] = useState("");
  const [regulationType, setRegulationType] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      await onUpload(file, {
        folderId: folderId === "none" ? null : folderId,
        jurisdiction: jurisdiction || undefined,
        regulationType: regulationType || undefined,
      });
      resetAndClose();
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFile(null);
    setFolderId(defaultFolderId ?? "none");
    setJurisdiction("");
    setRegulationType("");
    onOpenChange(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={open => { if (!open) resetAndClose(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Upload className="h-4 w-4 text-primary" />
            </div>
            Upload Document
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Drop zone */}
          <div
            className={cn(
              "relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-colors",
              dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30",
              file && "border-primary/40 bg-primary/5"
            )}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center gap-3 w-full">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => setFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground mb-3" />
                <p className="text-sm font-medium">Drop a file here or <label htmlFor="kb-file-input" className="text-primary cursor-pointer hover:underline">browse</label></p>
                <p className="text-xs text-muted-foreground mt-1">PDF, TXT up to 50 MB</p>
              </>
            )}
            <input
              id="kb-file-input"
              type="file"
              accept="application/pdf,.pdf,text/plain,.txt"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleFileChange}
            />
          </div>

          {/* Folder */}
          <div className="space-y-2">
            <Label>Folder</Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No folder (unfiled)</SelectItem>
                {folders.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Regulation type */}
            <div className="space-y-2">
              <Label>Regulation type <span className="text-muted-foreground">(optional)</span></Label>
              <Select value={regulationType} onValueChange={setRegulationType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {REGULATION_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Jurisdiction */}
            <div className="space-y-2">
              <Label>Jurisdiction <span className="text-muted-foreground">(optional)</span></Label>
              <Select value={jurisdiction} onValueChange={setJurisdiction}>
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {JURISDICTIONS.map(j => (
                    <SelectItem key={j} value={j}>{j}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleUpload} disabled={!file || loading}>
            {loading ? "Uploading…" : "Upload & index"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
